import { Check } from "lucide-react";
import { waLink, waPackageMsg } from "@/lib/wa";
import { useAuth, trackInterest } from "@/lib/auth";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { FadeUp, SectionHeading, WhatsAppIcon } from "./Shared";

const PLANS = [
    { key: "basic", name: "Basic", price: "Rp 5.000.000", highlight: false },
    { key: "profesional", name: "Profesional", price: "Rp 10.000.000", highlight: true },
    { key: "luxury", name: "Luxury", price: "Rp 50.000.000", highlight: false },
];

const PLAN_IMAGES = {
    basic: "https://images.unsplash.com/photo-1552558636-f6a8f071c2b3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNDR8MHwxfHNlYXJjaHwyfHxjb3p5JTIwc2ltcGxlJTIwd2FybSUyMGJlZHJvb218ZW58MHx8fHwxNzkyNjM2NzgxfDA&ixlib=rb-4.1.0&q=85",
    profesional:
        "https://images.pexels.com/photos/8134753/pexels-photo-8134753.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    luxury: "https://images.pexels.com/photos/37526426/pexels-photo-37526426.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const PlanButton = ({ plan, label }) => {
    const { user } = useAuth();
    return (
        <a
            href={waLink(waPackageMsg(plan.name, plan.price))}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackInterest(user, plan.name)}
            data-testid={`pricing-select-${plan.key}`}
            className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform duration-200 hover:scale-[1.03] ${
                plan.highlight
                    ? "btn-flame text-white shadow-lg shadow-orange-900/40"
                    : "border border-white/15 bg-white/5 text-slate-100 hover:border-amber-400/50"
            }`}
        >
            <WhatsAppIcon className="h-4 w-4" />
            {label}
        </a>
    );
};

export const Pricing = () => {
    const { lang } = useLang();
    const t = STR.pricing[lang];

    return (
        <section id="paket" data-testid="pricing-section" className="relative py-24 md:py-32">
            <div
                className="absolute left-1/2 top-1/3 h-72 w-[36rem] -translate-x-1/2 rounded-full bg-red-600/10 blur-[120px]"
                aria-hidden="true"
            />
            <div className="relative mx-auto max-w-7xl px-5 md:px-8">
                <SectionHeading eyebrow={t.eyebrow} title={t.title} subtitle={t.subtitle} />
                <div className="grid grid-cols-1 items-stretch gap-8 md:grid-cols-3">
                    {PLANS.map((plan, i) => {
                        const info = t.plans[plan.key];
                        return (
                            <FadeUp key={plan.key} delay={i * 0.1} className="h-full">
                                <article
                                    data-testid={`pricing-card-${plan.key}`}
                                    className={`relative flex h-full flex-col rounded-2xl p-8 transition-transform duration-300 hover:-translate-y-2 ${
                                        plan.highlight
                                            ? "border border-orange-500/60 bg-[#161210] shadow-[0_0_50px_rgba(234,88,12,0.18)] md:scale-105"
                                            : "card-surface"
                                    }`}
                                >
                                    {plan.highlight && (
                                        <span className="btn-flame absolute -top-3.5 left-1/2 z-10 -translate-x-1/2 rounded-full px-4 py-1 text-xs font-bold uppercase tracking-wider text-white">
                                            {t.popular}
                                        </span>
                                    )}
                                    <div className="relative -mx-8 -mt-8 mb-6 overflow-hidden">
                                        <img
                                            src={PLAN_IMAGES[plan.key]}
                                            alt={`Contoh properti untuk Paket ${plan.name}`}
                                            loading="lazy"
                                            className="aspect-video w-full object-cover transition-transform duration-500 hover:scale-105"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
                                    </div>
                                    <h3 className="text-xl font-bold text-slate-50">{plan.name}</h3>
                                    <p className="mt-2 text-sm text-zinc-400">{info.desc}</p>
                                    <p
                                        data-testid={`pricing-price-${plan.key}`}
                                        className={`mt-6 text-3xl font-extrabold tracking-tight ${
                                            plan.highlight ? "text-gradient" : "text-slate-50"
                                        }`}
                                    >
                                        {plan.price}
                                    </p>
                                    <ul className="mt-8 flex-1 space-y-3">
                                        {info.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2.5 text-sm text-zinc-300">
                                                <span
                                                    className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                                                        plan.highlight ? "bg-orange-500/20" : "bg-white/5"
                                                    }`}
                                                >
                                                    <Check className="h-3 w-3 text-amber-400" />
                                                </span>
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <PlanButton plan={plan} label={t.choose} />
                                </article>
                            </FadeUp>
                        );
                    })}
                </div>
                <FadeUp delay={0.2}>
                    <p data-testid="pricing-note" className="mx-auto mt-10 max-w-2xl text-center text-sm text-zinc-500">
                        {t.note}
                    </p>
                </FadeUp>
            </div>
        </section>
    );
};
